import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  UserGroupIcon,
  ClockIcon,
  StarIcon,
  SparklesIcon,
  ChartBarIcon,
  UserIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { Bar, Radar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, 
  LinearScale, PointElement, LineElement, RadialLinearScale, 
  Title, Tooltip, Legend, Filler } from 'chart.js';

// Registrar os componentes necessários do Chart.js
ChartJS.register(
  ArcElement, 
  BarElement,
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  RadialLinearScale,
  Title, 
  Tooltip, 
  Legend,
  Filler
);

const TeamSection = ({ metrics, isMobile }) => {
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  
  // Lista simulada de profissionais
  const professionals = [
    { 
      id: 1, 
      name: 'Ana Silva', 
      role: 'Cabeleireira',
      avatar: '👩‍💇‍♀️',
      appointmentsCount: 86,
      appointmentsPercentage: 32,
      favoriteCount: 42,
      rating: 4.9,
      revenue: 6200,
      revenuePercentage: 38,
      avgTime: 45,
      skills: [95, 90, 85, 70, 80]
    },
    { 
      id: 2, 
      name: 'João Santos', 
      role: 'Barbeiro',
      avatar: '💇‍♂️',
      appointmentsCount: 72,
      appointmentsPercentage: 28,
      favoriteCount: 38,
      rating: 4.7,
      revenue: 5100,
      revenuePercentage: 31,
      avgTime: 30,
      skills: [80, 95, 75, 85, 70]
    },
    { 
      id: 3, 
      name: 'Carla Oliveira', 
      role: 'Manicure',
      avatar: '💅',
      appointmentsCount: 65,
      appointmentsPercentage: 24,
      favoriteCount: 30,
      rating: 4.8,
      revenue: 3200,
      revenuePercentage: 19,
      avgTime: 50,
      skills: [70, 75, 95, 90, 85]
    },
    { 
      id: 4, 
      name: 'Rafael Lima', 
      role: 'Maquiador',
      avatar: '👨‍🎨',
      appointmentsCount: 42,
      appointmentsPercentage: 16,
      favoriteCount: 25,
      rating: 4.6,
      revenue: 2000,
      revenuePercentage: 12,
      avgTime: 60,
      skills: [65, 70, 80, 95, 90]
    }
  ];
  
  // Dados para o gráfico de agendamentos por profissional
  const appointmentsData = {
    labels: professionals.map(p => p.name),
    datasets: [
      {
        label: 'Agendamentos',
        data: professionals.map(p => p.appointmentsCount),
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para o gráfico de faturamento por profissional
  const revenueData = {
    labels: professionals.map(p => p.name),
    datasets: [
      {
        label: 'Faturamento (R$)',
        data: professionals.map(p => p.revenue),
        backgroundColor: 'rgba(34, 197, 94, 0.7)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para o gráfico de habilidades do profissional selecionado
  const getSkillsData = (professional) => {
    return {
      labels: ['Técnica', 'Agilidade', 'Atendimento', 'Pontualidade', 'Inovação'],
      datasets: [
        {
          label: 'Competências',
          data: professional.skills,
          backgroundColor: 'rgba(139, 92, 246, 0.3)',
          borderColor: 'rgb(139, 92, 246)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(139, 92, 246)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgb(139, 92, 246)',
          pointRadius: 3,
        }
      ]
    };
  };
  
  // Gráfico de distribuição do tempo
  const timeDistributionData = {
    labels: ['Atendimento', 'Preparação', 'Finalização', 'Tempo ocioso'],
    datasets: [
      {
        data: [60, 15, 15, 10],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)', // azul
          'rgba(139, 92, 246, 0.7)', // roxo
          'rgba(34, 197, 94, 0.7)',  // verde
          'rgba(209, 213, 219, 0.7)' // cinza
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(139, 92, 246)',
          'rgb(34, 197, 94)',
          'rgb(209, 213, 219)'
        ],
        borderWidth: 1,
      }
    ]
  };

  // Opções para gráficos
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };
  
  const radarOptions = {
    responsive: true,
    scales: {
      r: {
        angleLines: {
          display: true
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };
  
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    cutout: '65%'
  };
  
  // Obter dados do profissional selecionado ou o mais popular
  const selectedProfessionalData = selectedProfessional 
    ? professionals.find(p => p.id === selectedProfessional) 
    : professionals[0];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Eficiência do Time</h2>
      
      {/* Cards de estatísticas da equipe */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Profissionais</h3>
            <div className="bg-indigo-100 p-2 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-indigo-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{professionals.length}</p>
          <p className="mt-1 text-xs text-gray-500">ativos no período</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Prof. mais agendado</h3>
            <div className="bg-blue-100 p-2 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-800 truncate">{professionals[0].name}</p>
          <div className="mt-1 flex items-center text-xs">
            <span className="text-blue-600 font-medium">{professionals[0].appointmentsCount} agendamentos</span>
            <span className="text-gray-400 ml-1">({professionals[0].appointmentsPercentage}%)</span>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Tempo médio</h3>
            <div className="bg-purple-100 p-2 rounded-lg">
              <ClockIcon className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {Math.round(professionals.reduce((sum, p) => sum + p.avgTime, 0) / professionals.length)}min
          </p>
          <p className="mt-1 text-xs text-gray-500">por atendimento</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Avaliação média</h3>
            <div className="bg-amber-100 p-2 rounded-lg">
              <StarIcon className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {(professionals.reduce((sum, p) => sum + p.rating, 0) / professionals.length).toFixed(1)}
          </p>
          <div className="mt-1 flex items-center text-xs">
            <div className="flex text-amber-500">
              <StarIcon className="h-3 w-3 fill-current" />
              <StarIcon className="h-3 w-3 fill-current" />
              <StarIcon className="h-3 w-3 fill-current" />
              <StarIcon className="h-3 w-3 fill-current" />
              <StarIcon className="h-3 w-3 fill-current" />
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Gráficos de comparação da equipe */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Agendamentos por Profissional</h3>
          <div className="h-64">
            <Bar data={appointmentsData} options={barOptions} />
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <h3 className="text-lg font-semibold mb-4">Faturamento por Profissional</h3>
          <div className="h-64">
            <Bar data={revenueData} options={barOptions} />
          </div>
        </motion.div>
      </div>
      
      {/* Detalhe do profissional selecionado */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Detalhes do Profissional</h3>
          <select 
            value={selectedProfessional || professionals[0].id}
            onChange={(e) => setSelectedProfessional(Number(e.target.value))}
            className="py-1 px-3 border border-gray-300 rounded-md text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500"
          >
            {professionals.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="col-span-1">
            <div className="flex flex-col items-center p-4 bg-indigo-50 rounded-xl">
              <div className="text-4xl mb-2">{selectedProfessionalData.avatar}</div>
              <h4 className="text-lg font-medium text-gray-800">{selectedProfessionalData.name}</h4>
              <p className="text-sm text-gray-500">{selectedProfessionalData.role}</p>
              
              <div className="w-full mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Agendamentos</span>
                  <span className="font-medium">{selectedProfessionalData.appointmentsCount}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full" 
                    style={{ width: `${selectedProfessionalData.appointmentsPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="w-full mt-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Favoritos</span>
                  <span className="font-medium">{selectedProfessionalData.favoriteCount}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full" 
                    style={{ width: `${(selectedProfessionalData.favoriteCount / selectedProfessionalData.appointmentsCount) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="w-full mt-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Faturamento</span>
                  <span className="font-medium">R$ {selectedProfessionalData.revenue}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${selectedProfessionalData.revenuePercentage}%` }}
                  ></div>
                </div>
              </div>
              
              <button className="mt-4 flex items-center text-indigo-600 text-sm font-medium">
                <PencilSquareIcon className="h-4 w-4 mr-1" />
                Editar metas
              </button>
            </div>
          </div>
          
          <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Competências e Habilidades</h4>
              <div className="h-64 p-2">
                <Radar 
                  data={getSkillsData(selectedProfessionalData)} 
                  options={radarOptions} 
                />
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Distribuição de Tempo</h4>
              <div className="h-64 flex items-center justify-center">
                <Doughnut 
                  data={timeDistributionData} 
                  options={doughnutOptions} 
                />
              </div>
            </div>
            
            <div className="md:col-span-2 bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-start">
                <ChartBarIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Sugestões de Melhoria</p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-start">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1"></div>
                      <p className="text-xs text-blue-700">
                        Reduzir o tempo de preparação em 5 minutos, aumentando a eficiência por cliente.
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1"></div>
                      <p className="text-xs text-blue-700">
                        Incentivar cross-selling de produtos, com meta de 15% de conversão.
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1"></div>
                      <p className="text-xs text-blue-700">
                        Implementar treinamento em nova técnica para aumentar ticket médio em 20%.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TeamSection; 